import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  ClockIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import styles from './Contacto.module.css'

export default function Contacto() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false)
      setIsSubmitted(true)
      console.log('Form submitted:', formData)
      
      // Reset form after success
      setTimeout(() => {
        setIsSubmitted(false)
        setFormData({ name: '', email: '', company: '', message: '' })
      }, 3000)
    }, 2000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const phoneNumber = '3126825961'
  const whatsappMessage = encodeURIComponent('¡Hola! Me interesa conocer más sobre los servicios de Royal Software SAS. ¿Podrían ayudarme?')

  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={styles.header}
        >
          <h1 className={styles.title}>Hablemos de tu Proyecto</h1>
          <p className={styles.subtitle}>
            Cuéntanos sobre tu visión y te ayudaremos a convertirla en realidad con soluciones tecnológicas innovadoras
          </p>
        </motion.div>
        <div className={styles.contentGrid}>
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={styles.formSection}
          >
            <form onSubmit={handleSubmit} className={`${styles.form} ${isSubmitting ? styles.loading : ''}`}>
              <div className={styles.formFields}>
                <div className={styles.fieldGroup}>
                  <label htmlFor="name" className={styles.label}>Nombre Completo *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={styles.input} placeholder="Tu nombre completo" required disabled={isSubmitting} />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="email" className={styles.label}>Correo Electrónico *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={styles.input} placeholder="tu@email.com" required disabled={isSubmitting} />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="company" className={styles.label}>Empresa</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={styles.input} placeholder="Nombre de tu empresa" disabled={isSubmitting} />
                </div>
                <div className={styles.fieldGroup}>
                  <label htmlFor="message" className={styles.label}>Cuéntanos sobre tu proyecto *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} className={styles.textarea} placeholder="Describe tu proyecto, necesidades, objetivos y cualquier detalle relevante..." required disabled={isSubmitting} />
                </div>
                <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                  {isSubmitting && <div className={styles.loadingSpinner} />}
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
                {isSubmitted && (
                  <div className={styles.successMessage}>
                    <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', display: 'inline', marginRight: '0.5rem' }} />
                    ¡Mensaje enviado con éxito! Te contactaremos pronto.
                  </div>
                )}
              </div>
            </form>
          </motion.div>
          {/* Contact Information Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className={styles.contactSection}
          >
            {/* Contact Info Card */}
            <div className={styles.contactCard}>
              <h3 className={styles.cardTitle}>
                <div className={styles.cardIcon}><EnvelopeIcon style={{ width: '1.25rem', height: '1.25rem' }} /></div>
                Información de Contacto
              </h3>
              <div className={styles.contactInfo}>
                <div className={styles.infoItem}>
                  <h4 className={styles.infoLabel}>Email Corporativo</h4>
                  <a href="mailto:contacto@royalsoftware.com" className={styles.infoValue} style={{ textDecoration: 'none', color: 'inherit' }}>contacto@royalsoftware.com</a>
                </div>
                <div className={styles.infoItem}>
                  <h4 className={styles.infoLabel}>Teléfono Principal</h4>
                  <a href={`tel:+57${phoneNumber}`} className={`${styles.infoValue} ${styles.phoneNumber}`} style={{ textDecoration: 'none', color: 'inherit' }}>+57 {phoneNumber}</a>
                </div>
                <div className={styles.infoItem}>
                  <h4 className={styles.infoLabel}>Ubicación</h4>
                  <p className={styles.infoValue}><MapPinIcon style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />Colombia, Latinoamérica.</p>
                </div>
                <div className={styles.infoItem}>
                  <h4 className={styles.infoLabel}>Horario de Atención</h4>
                  <p className={styles.infoValue}><ClockIcon style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />Lunes a Viernes: 8:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>
            <div className={`${styles.contactCard} ${styles.whatsappCard}`}>
              <h3 className={styles.cardTitle}>
                <div className={styles.cardIcon} style={{ background: 'linear-gradient(135deg, #25d366, #22c55e)' }}><ChatBubbleLeftRightIcon style={{ width: '1.25rem', height: '1.25rem' }} /></div>
                ¿Prefieres WhatsApp?
              </h3>
              <p className={styles.whatsappDescription}>Escríbenos directamente y te responderemos en minutos. Ideal para consultas rápidas y soporte inmediato.</p>
              <a href={`https://wa.me/57${phoneNumber}?text=${whatsappMessage}`} target="_blank" rel="noopener noreferrer" className={styles.whatsappButton}>
                <svg className={styles.whatsappIcon} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                Chatear en WhatsApp
              </a>
              <div className={styles.featuresGrid}>
                <div className={styles.featureItem}><div className={styles.featureIcon}><ClockIcon style={{ width: '1rem', height: '1rem' }} /></div><span className={styles.featureText}>Respuesta rápida</span></div>
                <div className={styles.featureItem}><div className={styles.featureIcon}><UserGroupIcon style={{ width: '1rem', height: '1rem' }} /></div><span className={styles.featureText}>Soporte personalizado</span></div>
                <div className={styles.featureItem}><div className={styles.featureIcon}><ShieldCheckIcon style={{ width: '1rem', height: '1rem' }} /></div><span className={styles.featureText}>Comunicación segura</span></div>
                <div className={styles.featureItem}><div className={styles.featureIcon}><CheckCircleIcon style={{ width: '1rem', height: '1rem' }} /></div><span className={styles.featureText}>Sin compromiso</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
